import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, Dialog, IconButton, Tooltip } from '@material-ui/core'
import {
  Settings as SettingsIcon,
  // UploadCloud as UploadCloudIcon,
} from 'react-feather'
import { OndemandVideo as OndemandVideoIcon } from '@material-ui/icons'

import { map, filter, flattenDeep } from 'lodash'

import {
  Container,
  ContentHeader,
  LoadingFeedback,
  Page,
  PermittedByAssignmentRole,
} from 'components'
import {
  FinalizeDialog,
  // AlertSendToSegcoop,
  ImportProgressAlert,
  InformationCard,
  ProjectBoard,
  ProjectSettingsModal,
  // SendToSegcoopDialog,
} from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const GovernanceProjectsManage = ({ match }) => {
  const { governanceProjectId } = match.params
  const {
    PENDING_STATUS_ID,
    COMPLETED_STATUS_ID,
    OVERDUE_STATUS_ID,
    MANAGER_ROLE,
    PROJECT_RESPONSIBLE_ROLE,
    IMPORT_IN_PROGRESS_STATUSES,
  } = constants.governanceProject
  const { GOVERNANCE_PROJECTS } = constants.permissions

  const [openModal, setOpenModal] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [openFinalizedModal, setOpenFinalizedModal] = useState(false)
  // const [openSendToSegcoopDialog, setOpenSendToSegcoopDialog] = useState(false)

  const [loading, setLoading] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const {
    response: projectResponse,
    isLoading: projectIsLoading,
    refresh: projectRefresh,
  } = useFetch(
    service.dponet.governanceProject.get,
    {
      governanceProjectId,
    },
    [],
  )

  const governanceProject = projectResponse?.data?.governanceProject

  const [importData, setImportData] = useState(null)

  useEffect(() => {
    setImportData(governanceProject?.governanceProjectImport ?? null)
  }, [governanceProject?.governanceProjectImport])

  const importStatus = importData?.status
  const isImportInProgress = IMPORT_IN_PROGRESS_STATUSES.includes(importStatus)
  const isImportActive = !!importStatus && importStatus !== 'completed'

  const projectRefreshRef = useRef(null)
  projectRefreshRef.current = projectRefresh

  useEffect(() => {
    if (!isImportInProgress) return

    const intervalId = setInterval(async () => {
      try {
        const response = await service.dponet.governanceProject.getImportStatus(
          { governanceProjectId },
        )
        if (response.status === 204) return

        const freshImport = response?.data?.governanceProjectImport
        setImportData(freshImport)

        if (
          constants.governanceProject.IMPORT_TERMINAL_STATUSES.includes(
            freshImport?.status,
          )
        ) {
          projectRefreshRef.current()
        }
      } catch (error) {
        // silencioso — não interrompe o polling por falha pontual
      }
    }, 15000)

    return () => clearInterval(intervalId)
  }, [isImportInProgress, governanceProjectId])

  const {
    response: questionnairesResponse,
    isLoading: questionnairesIsLoading,
    refresh: questionnairesRefresh,
  } = useFetch(
    service.dponet.governanceProjectQuestionnaire.resume,
    {
      governanceProjectId,
      order: 'kind ASC',
    },
    [],
  )

  const questionnaires =
    questionnairesResponse?.data?.governanceProjectQuestionnaires

  const { response: usersResponse, isLoading: usersIsLoading } = useFetch(
    service.dponet.users.get,
    {
      perPage: 10000,
      minimal: true,
      active: true,
      userCompanyStatus: constants.userCompanies.ACTIVE,
    },
  )

  const users = usersResponse?.data?.users

  const { response: templateResponse, isLoading: templateIsLoading } = useFetch(
    service.dponet.governanceProjectTemplate.get,
    {},
    [],
  )

  const projectTemplates = templateResponse?.data?.governanceProjectTemplates

  const requestIsLoading =
    projectIsLoading ||
    questionnairesIsLoading ||
    usersIsLoading ||
    templateIsLoading

  const handleOpenModal = () => setOpenModal((prevState) => !prevState)
  const handleOpenDialog = () => setOpenDialog((prevState) => !prevState)

  const handleRefresh = () => {
    projectRefresh()
    questionnairesRefresh()
  }

  const handleOpenFinalizedModal = () =>
    setOpenFinalizedModal((prevState) => !prevState)

  const handleFinalizedProject = async () => {
    try {
      setLoading(true)

      await service.dponet.governanceProject.changeStatus({
        governanceProjectId: governanceProject.id,
        status: COMPLETED_STATUS_ID,
      })

      snackbar.open({
        message: 'Projeto finalizado com sucesso!',
        variant: 'success',
      })
      handleRefresh()
      handleOpenFinalizedModal()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const notFinishedActivities = flattenDeep(
    map(questionnaires, (questionnaire) =>
      map(
        filter(
          questionnaire.governanceProjectActivities,
          (activity) => activity.status !== COMPLETED_STATUS_ID,
        ),
        (activity) => ({ id: activity.id, name: activity.theme }),
      ),
    ),
  )

  // const handleOpenSendToSegcoopDialog = () =>
  //   setOpenSendToSegcoopDialog((prevState) => !prevState)

  // const segcoopQueue = governanceProject?.governanceProjectSegcoopQueue
  // const segcoopQueueStatus = segcoopQueue?.status
  // const segcoopQueueStatusLabel =
  //   constants.governanceProject.SEGCOOP_QUEUE_STATUS_LABELS[segcoopQueueStatus]
  // const showSegcoopButton =
  //   governanceProject?.canSendToSegcoop && !!governanceProject && !segcoopQueue

  // const segcoopTooltipTitle =
  //   constants.governanceProject.SEGCOOP_QUEUE_STATUS_TOOLTIP[
  //     segcoopQueueStatus
  //   ] || 'Enviar para o Segcoop'

  return (
    <Page title={governanceProject?.name || 'Projeto de Governança'}>
      <LoadingFeedback open={loading} />
      <Container maxWidth={false}>
        <ContentHeader
          title={governanceProject?.name || 'Projeto de Governança'}
          goBack
        >
          <Box width="100%" display="flex" gridGap={4}>
            {/* {showSegcoopButton && (
              <Tooltip title={segcoopTooltipTitle}>
                <Box>
                  <Button
                    variant="contained"
                    style={{
                      backgroundColor: '#2196f3',
                      color: '#fff',
                    }}
                    startIcon={<UploadCloudIcon />}
                    onClick={handleOpenSendToSegcoopDialog}
                    fullWidth
                  >
                    {segcoopQueueStatusLabel || 'Enviar para o Segcoop'}
                  </Button>
                </Box>
              </Tooltip>
            )} */}
            <Tooltip title="Iniciar o vídeo tutorial">
              <Button
                variant="contained"
                color="primary"
                startIcon={<OndemandVideoIcon />}
                onClick={handleOpenDialog}
                fullWidth
              >
                Vídeo Tutorial
              </Button>
            </Tooltip>
          </Box>
          <Box className={classes.settingsContainer}>
            <PermittedByAssignmentRole
              permittedRoles={[MANAGER_ROLE, PROJECT_RESPONSIBLE_ROLE]}
              userRole={governanceProject?.assignmentRole}
              createdBySystem={governanceProject?.createdBySystem}
              someTags={[
                GOVERNANCE_PROJECTS.CREATE,
                GOVERNANCE_PROJECTS.CHANGE_STATUS,
              ]}
            >
              {[PENDING_STATUS_ID, OVERDUE_STATUS_ID].includes(
                governanceProject?.status,
              ) && (
                <Box width="100%">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenFinalizedModal}
                    disabled={isImportActive}
                    fullWidth
                  >
                    Finalizar projeto
                  </Button>
                </Box>
              )}
              <Tooltip title="Configurações">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleOpenModal}
                    disabled={isImportActive}
                  >
                    <SettingsIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </PermittedByAssignmentRole>
          </Box>
        </ContentHeader>
        {/* {!!segcoopQueueStatus && (
          <Box mb={2} onClick={handleOpenSendToSegcoopDialog}>
            <AlertSendToSegcoop status={segcoopQueueStatus} />
          </Box>
        )} */}
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <InformationCard
            governanceProject={governanceProject}
            questionnaires={questionnaires}
            requestIsLoading={requestIsLoading}
          />
          <ImportProgressAlert
            governanceProjectImport={importData}
            governanceProjectId={governanceProjectId}
            onRetry={projectRefresh}
          />
          {!isImportInProgress && (
            <ProjectBoard
              questionnaires={questionnaires}
              governanceProject={governanceProject}
              requestIsLoading={requestIsLoading}
            />
          )}
        </Box>
        {openModal && (
          <ProjectSettingsModal
            open={openModal}
            setOpen={handleOpenModal}
            setLoading={setLoading}
            isEdit
            users={users}
            refresh={handleRefresh}
            governanceProject={governanceProject}
            projectTemplates={projectTemplates}
          />
        )}
        {openFinalizedModal && (
          <FinalizeDialog
            open={openFinalizedModal}
            handleCloseFinalized={handleOpenFinalizedModal}
            handleFinalize={handleFinalizedProject}
            pendingActivities={notFinishedActivities || []}
          />
        )}
      </Container>
      <Dialog open={openDialog} onClose={handleOpenDialog} maxWidth="md">
        <Box p={2} width="100%" display="flex" justifyContent="center">
          <iframe
            className={classes.videoPlayer}
            src="https://player.vimeo.com/video/1074767910?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1"
            title="Vídeo tutorial de projetos de governanças"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      </Dialog>
      {/* <SendToSegcoopDialog
        open={openSendToSegcoopDialog}
        handleOpen={handleOpenSendToSegcoopDialog}
        refresh={handleRefresh}
        setLoading={setLoading}
        governanceProjectId={governanceProjectId}
        segcoopQueue={segcoopQueue}
      /> */}
    </Page>
  )
}

export default GovernanceProjectsManage
